import { useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpBarBase from 'pages/ApplicationRequest/components/containers/1616BridgesEpic/components/HelpBar';

const HelpBar = ({ open, setOpen }) => {
	const [sExpandStart, setExpandStart] = useState(true);
	const [sExpandActions, setExpandActions] = useState(false);
	const [sExpandErrors, setExpandErrors] = useState(false);
	return (
		<HelpBarBase open={open} setOpen={setOpen} sxPaper={{ height: '95%' }}>
			<Typography variant='h6' color='primary' gutterBottom>
				Bulk Job Category Upload
			</Typography>
			<Typography variant='subtitle2' gutterBottom>
				This tool can be used to add, modify, or delete users' job
				category assignments across Epic instances.
			</Typography>
			<Typography variant='subtitle2' gutterBottom>
				At this time, uploads do not trigger automation in the same manner
				that Manager Validations does.
			</Typography>
			<Divider orientation='horizontal' sx={{ marginBottom: '.35em' }} />
			<Box
				display='flex'
				justifyContent='space-between'
				alignItems='center'
			>
				<Typography variant='subtitle1' gutterBottom>
					Getting Started
				</Typography>
				<IconButton onClick={() => setExpandStart(expand => !expand)}>
					{sExpandStart ? <ExpandLessIcon /> : <ExpandMoreIcon />}
				</IconButton>
			</Box>
			{sExpandStart && (
				<>
					<Typography variant='subtitle2' gutterBottom>
						Please begin by downloading the CSV template to adhere
						to the upload expectations. The data type for each
						column is <i>number</i>; for columns <b>Experienced</b>{' '}
						and <b>Validated</b>, <b>1</b> for "true" and <b>0</b>{' '}
						for "false".
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						The following columns are required per upload row:
						<ul>
							<li>
								<b>Report ID</b>
								<ul>
									<li>
										This data point is published by IMS SSRS
										reports.
									</li>
								</ul>
							</li>
							<li>
								<b>Old Job Category ID</b> (OR){' '}
								<b>Job Category ID</b>
							</li>
							<li>
								<b>Wave</b>
								<ul>
									<li>
										Use -1 for any Epic instance whose
										rollout is not by wave.
									</li>
								</ul>
							</li>
						</ul>
						Failure to satisfy those criteria will result in the row
						being skipped. Columns <b>Experienced</b> and{' '}
						<b>Validated</b> will default to "false" when left
						undefined.
					</Typography>
				</>
			)}
			<Divider orientation='horizontal' sx={{ marginBottom: '.35em' }} />
			<Box
				display='flex'
				justifyContent='space-between'
				alignItems='center'
			>
				<Typography variant='subtitle1' gutterBottom>
					Supported Actions
				</Typography>
				<IconButton onClick={() => setExpandActions(expand => !expand)}>
					{sExpandActions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
				</IconButton>
			</Box>
			{sExpandActions && (
				<>
					<Typography
						variant='subtitle2'
						sx={{ marginBottom: '16px' }}
					>
						The following actions can be taken against a user's job
						category assignment(s). Please note that columns{' '}
						<b>Experienced</b> and <b>Validated</b> may be defined
						for each.
					</Typography>
					<Typography
						color='secondary.main'
						variant='subtitle1'
						gutterBottom
					>
						<b>Add</b>
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						This adds a new job category to the user's existing
						assignments if it does not exist.
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						<i>Syntax</i>: omit column <b>Old Job Category ID</b>;
						define column <b>Job Category ID</b>
					</Typography>
					<Divider
						orientation='horizontal'
						sx={{ marginBottom: '.35em' }}
					/>
					<Typography
						color='secondary.main'
						variant='subtitle1'
						gutterBottom
					>
						<b>Replace</b>
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						This replaces a user's existing job category.
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						<i>Syntax</i>: define column <b>Old Job Category ID</b>{' '}
						and column <b>Job Category ID</b>
					</Typography>
					<Divider
						orientation='horizontal'
						sx={{ marginBottom: '.35em' }}
					/>
					<Typography
						color='secondary.main'
						variant='subtitle1'
						gutterBottom
					>
						<b>Edit</b>
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						This edits the defined attributes of an existing job
						category assignment if it exists.
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						<i>Syntax</i>: omit column <b>Old Job Category ID</b>;
						define column <b>Job Category ID</b>
					</Typography>
					<Divider
						orientation='horizontal'
						sx={{ marginBottom: '.35em' }}
					/>
					<Typography
						color='secondary.main'
						variant='subtitle1'
						gutterBottom
					>
						<b>Delete</b>
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						This deletes an existing job category assignment. Wave
						is still required but not considered when determining
						which assignment to delete.
					</Typography>
					<Typography variant='subtitle2' gutterBottom>
						<i>Syntax</i>: define column <b>Old Job Category ID</b>;
						omit column <b>Job Category ID</b>
					</Typography>
				</>
			)}
			<Divider orientation='horizontal' sx={{ marginBottom: '.35em' }} />
			<Box
				display='flex'
				justifyContent='space-between'
				alignItems='center'
			>
				<Typography variant='subtitle1' gutterBottom>
					Error Handling
				</Typography>
				<IconButton onClick={() => setExpandErrors(expand => !expand)}>
					{sExpandErrors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
				</IconButton>
			</Box>
			{sExpandErrors && (
				<>
					<Typography
						variant='subtitle2'
						sx={{ marginBottom: '16px' }}
					>
						If the upload contains rows with invalid values, they
						will be rendered and made available for inline
						correction if desired.
					</Typography>
					<Typography
						variant='subtitle2'
						sx={{ marginBottom: '16px' }}
					>
						For reach row, click the row's leading icon to attempt
						row re-submission after corrections are made.
					</Typography>
				</>
			)}
			<Divider orientation='horizontal' sx={{ marginBottom: '.35em' }} />
		</HelpBarBase>
	);
};

export { HelpBar };
